/**
 * Mailchimp newsletter signup.
 *
 * Ports app/composables/useMailchimp.ts: the subscribe request is a JSONP call
 * to https://<dc>.list-manage.com/subscribe/post-json with `c` as the callback
 * parameter, which is what lets a static storefront talk to Mailchimp without
 * a server-side proxy (the endpoint sends no CORS headers).
 *
 * Validation mirrors the vuelidate rules: `fname` required, `email` required
 * and well formed.
 */
(function () {
  'use strict';

  // Theme settings → Integrations → Mailchimp form host; the fallback is the
  // account's current datacentre, for markup rendered before the setting
  // existed.
  var DEFAULT_HOST = 'figursky.us7.list-manage.com';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var counter = 0;

  function endpointFor(form) {
    var host = (form.getAttribute('data-mailchimp-host') || '').trim() || DEFAULT_HOST;
    return 'https://' + host.replace(/^https?:\/\//, '').replace(/\/+$/, '') + '/subscribe/post-json';
  }

  /** JSONP — `jsonp(url, { param: 'c' }, cb)` in the composable. */
  function jsonp(url, callback) {
    counter += 1;
    // Timestamp + counter + random: the counter alone is per copy of this
    // file, so two copies on one page (a duplicated script tag, an app
    // embed) could mint the same global in the same millisecond.
    var name = 'figurskyMailchimp' + Date.now() + counter + Math.random().toString(36).slice(2, 8);
    var script = document.createElement('script');
    var timer = null;
    var done = false;

    function cleanup() {
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
      try {
        delete window[name];
      } catch (error) {
        window[name] = undefined;
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    }

    function finish(error, data) {
      if (done) {
        return;
      }
      done = true;
      cleanup();
      callback(error, data);
    }

    window[name] = function (data) {
      finish(null, data);
    };

    script.src = url + (url.indexOf('?') === -1 ? '?' : '&') + 'c=' + name;
    script.onerror = function () {
      finish(new Error('Could not reach the mailing list. Please try again.'), null);
    };

    timer = window.setTimeout(function () {
      finish(new Error('The request timed out. Please try again.'), null);
    }, 15000);

    document.body.appendChild(script);
  }

  /** `formatErrorMessage` in the composable. */
  function formatErrorMessage(message) {
    return String(message).replace('0 - ', '');
  }

  /**
   * Heuristic bot check, run after validation passes and the honeypot is
   * empty. Returning true drops the signup silently: the form shows its
   * normal success state and no request is sent, so the bot gets no signal
   * to adapt to.
   *
   * fname     — trimmed name field
   * email     — trimmed email field
   * elapsedMs — time since the form was initialised (page load for the
   *             footer/section; the popup is initialised at load too, before
   *             it opens, so its elapsed time is always longer)
   */
  function looksLikeBot(fname, email, elapsedMs) {
    // Faster than even browser autofill plus a click.
    if (elapsedMs < 1500) {
      return true;
    }

    // The bots' FNAME is one random token: `bu9IEMtNZ1`, `VRXCOBDwU6`,
    // `HcjjQjpWsK`. Real names that mix case (McDonald, MacKenzie, DeAndre)
    // have at most one capital after the first letter; all-caps names
    // (`MOULI G`) have no lowercase; anything with a space is let through.
    if (/\s/.test(fname) || fname.length < 6) {
      return false;
    }
    var hasLower = /[a-z]/.test(fname);
    var innerCaps = (fname.slice(1).match(/[A-Z]/g) || []).length;
    var lettersAndDigits = /[a-z]/i.test(fname) && /\d/.test(fname);

    return hasLower && (innerCaps >= 2 || lettersAndDigits);
  }

  function setFieldError(input, hasError) {
    var field = input.closest('[data-newsletter-field]');
    if (!field) {
      return;
    }
    field.classList.toggle('has-error', hasError);
    var message = field.querySelector('[data-newsletter-field-error]');
    if (message) {
      message.hidden = !hasError;
    }
  }

  function init(root) {
    if (!root || root.hasAttribute('data-newsletter-ready')) {
      return;
    }

    var form = root.querySelector('[data-newsletter-form]');
    var fname = root.querySelector('[data-newsletter-fname]');
    var email = root.querySelector('[data-newsletter-email]');
    var fields = root.querySelector('[data-newsletter-fields]');
    var errorBox = root.querySelector('[data-newsletter-error]');
    var successBox = root.querySelector('[data-newsletter-success]');
    var idleLabel = root.querySelector('[data-newsletter-label-idle]');
    var loadingLabel = root.querySelector('[data-newsletter-label-loading]');
    var submit = root.querySelector('[data-newsletter-submit]');
    var honeypot = root.querySelector('[data-newsletter-honeypot]');

    if (!form || !fname || !email) {
      return;
    }

    root.setAttribute('data-newsletter-ready', 'true');

    var loading = false;
    var readyAt = Date.now();

    function setLoading(value) {
      loading = value;
      form.classList.toggle('is-loading', value);
      if (submit) {
        submit.disabled = value;
      }
      if (idleLabel) {
        idleLabel.hidden = value;
      }
      if (loadingLabel) {
        loadingLabel.hidden = !value;
      }
    }

    function showError(message) {
      if (!errorBox) {
        return;
      }
      // innerHTML on purpose: Mailchimp's `msg` is HTML, not text — the
      // "you're already subscribed" reply carries an <a> to the update-profile
      // page, and the Nuxt original rendered it the same way. The only sources
      // are Mailchimp's own response and this file's own error strings.
      errorBox.innerHTML = message;
      errorBox.hidden = false;
    }

    function clearError() {
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.innerHTML = '';
      }
    }

    /** The success state without the request — what a dropped bot sees. */
    function showSuccess() {
      if (fields) {
        fields.hidden = true;
      }
      if (successBox) {
        successBox.hidden = false;
      }
      fname.value = '';
      email.value = '';
    }

    function validate() {
      var nameOk = fname.value.trim().length > 0;
      var emailValue = email.value.trim();
      var emailOk = emailValue.length > 0 && EMAIL_RE.test(emailValue);
      setFieldError(fname, !nameOk);
      setFieldError(email, !emailOk);
      return nameOk && emailOk;
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      if (loading || !validate()) {
        return;
      }

      clearError();
      if (successBox) {
        successBox.hidden = true;
      }

      var trap = honeypot ? honeypot.value : '';
      if (trap !== '' || looksLikeBot(fname.value.trim(), email.value.trim(), Date.now() - readyAt)) {
        showSuccess();
        return;
      }

      setLoading(true);

      var query = [
        'u=' + encodeURIComponent(form.getAttribute('data-mailchimp-u') || ''),
        'id=' + encodeURIComponent(form.getAttribute('data-mailchimp-id') || ''),
        'EMAIL=' + encodeURIComponent(email.value.trim()),
        'FNAME=' + encodeURIComponent(fname.value.trim()),
      ];
      // Mailchimp's server-side honeypot: it rejects a non-empty value, so a
      // client that skips our check above still gets caught.
      if (honeypot) {
        query.push(encodeURIComponent(honeypot.name) + '=' + encodeURIComponent(trap));
      }
      query = query.join('&');

      jsonp(endpointFor(form) + '?' + query, function (error, data) {
        setLoading(false);

        if (error) {
          showError(error.message);
          return;
        }

        if (data && data.result === 'error') {
          showError(formatErrorMessage(data.msg));
          return;
        }

        showSuccess();
      });
    });

    [fname, email].forEach(function (input) {
      input.addEventListener('input', function () {
        setFieldError(input, false);
      });
    });
  }

  function initAll(scope) {
    var container = scope || document;
    Array.prototype.forEach.call(container.querySelectorAll('[data-newsletter]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
